import { InboxOutlined } from "@ant-design/icons";
import { Progress, Upload } from "antd";
import axios from "axios";
import _ from "lodash";
import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import Swal from "sweetalert2";

import { Button } from "@components/Button";
import { MemoizedReactPlayer } from "@components/MemoizedReactPlayer";
import { Text } from "@components/Text";
import { COLOR_GREEN, COLOR_GREY, COLOR_RED } from "@static/COLORS";
import { formatBytes } from "@utils/formatBytes";
import { generateDragMultipleUpload } from "@utils/generateDragMultipleUpload";
import { configHeader } from "@utils/https/configHeader";

const { Dragger } = Upload;

const Container = styled.div`
  width: 100%;
  height: 100%;
  /* overflow: scroll; */
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
  box-sizing: border-box;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-bottom: 8px;
  background-color: rgba(0, 0, 0, 0.1);
`;

const ContainerDrag = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  /* padding-bottom: 8px; */
  /* box-sizing: border-box; */
`;

const SectionDrag = styled.div`
  height: 100%;
  width: 100%;
  /* margin-bottom: 20px; */
`;

const ContainerPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
`;

const ContainerMediaPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  height: 100%;
  overflow: scroll;
`;

const ContainerMediaItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const MediaItem = styled.div`
  width: 170px;
  height: 120px;
  overflow: hidden;
  border: 1px dashed ${COLOR_GREY};
  border-radius: 8px;
  flex-shrink: 0;
`;

const ContainerAction = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 8px;
`;

const MediaContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
`;

const { REACT_APP_API_ENDPOINT, REACT_APP_REQUEST_TIME_OUT } = process.env;

export const FormMultipleUpload = ({
  customer_id,
  location_id,
  setMediaList = () => undefined,
  setDataList = () => undefined,
  setIsCloseModal = () => undefined,
  setIsModalVisible = () => undefined,
  mediaList = {},
  useInManageBlock,
}) => {
  console.log("mediaList :>> ", mediaList);
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const playerRefs = useRef([]);
  const [fileList, setFileList] = useState([]);
  const [progress, setProgress] = useState({});

  const base64ToFile = ({ base64, fileName, mimeType = "image/png" }) => {
    const byteString = atob(base64.split(",")[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([intArray], { type: mimeType });
    const file = new File([blob], fileName, {
      type: mimeType,
      lastModified: Date.now(),
    });

    return file;
  };

  const captureThumbnail = ({ index }) => {
    const player = playerRefs.current[index]?.getInternalPlayer();
    const canvas = canvasRef?.current;
    const ctx = canvas.getContext("2d");
    const width = 640;
    const height = 360;
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(player, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL();
    return dataUrl;
  };

  const onHandleConfirm = async () => {
    let uploadSuccessResults = [];
    setIsCloseModal(false);
    for (let index = 0; index < fileList.length; index++) {
      const item = fileList[index];
      const fileKey = _.get(item, ["fileKey"]);

      const isVideo = _.chain(item)
        .get(["type"])
        .split("/")
        .first()
        .isEqual("video")
        .value();

      const thumbnailBase64 = isVideo ? captureThumbnail({ index }) : null;
      const thumbnailFile = thumbnailBase64
        ? base64ToFile({
            base64: thumbnailBase64,
            fileName: `${fileKey}-thumbnail.png`,
          })
        : null;

      let isUploadComplete = false;

      try {
        const resp = await axios({
          method: "post",
          url: `${REACT_APP_API_ENDPOINT}${"/api/v1/medias-upload-file"}`,
          data: {
            category_customer_id: _.get(item, ["category_customer_id"]),
            location_id: _.get(item, ["location_id"]),
            display_name: _.get(item, ["display_name"]),
            extension: _.get(item, ["extension"]),
            type: _.get(item, ["type"]),
            duration: _.get(item, ["duration"]),
            size: _.get(item, ["size"]),
            file: _.get(item, ["file"]),
            thumbnailFile,
            fileKey,
            is_share: true,
          },

          headers: configHeader({ auth: true }),
          timeout: REACT_APP_REQUEST_TIME_OUT,
          onUploadProgress: (progressEvent) => {
            // Only update progress if upload is not complete
            if (!isUploadComplete) {
              const percentCompleted = Math.round(
                (progressEvent?.loaded * 100) / progressEvent?.total
              );
              // console.log(`percentCompleted :>> ${fileKey}`, percentCompleted);
              setProgress((prev) => {
                return { ...prev, [fileKey]: percentCompleted };
              });
            }
          },
        });
        isUploadComplete = true;
        setProgress((prev) => {
          return { ...prev, [fileKey]: 100 };
        });
        const uploadSuccessResult = _.get(resp, ["data", "result"]);
        uploadSuccessResults = [...uploadSuccessResults, uploadSuccessResult];
        if (!useInManageBlock) {
          setDataList((prev) => [...prev, uploadSuccessResult]);
          console.log("uploadSuccessResult :>> ", uploadSuccessResult);
        }
      } catch (error) {
        console.error(`Error uploading file ${index + 1}:`, error);
      }
    }

    if (useInManageBlock) {
      const groupUploadSuccessResults = _.chain(uploadSuccessResults)
        .map((item) => {
          const duration = _.chain(item).get(["duration"]).toInteger().value();
          const location_id = _.chain(item)
            .get(["location_id"])
            .toInteger()
            .value();
          const category_customer_id = _.chain(item)
            .get(["category_customer_id"])
            .toInteger()
            .value();

          const size = _.chain(item).get(["size"]).toInteger().value();
          return {
            ...item,
            duration,
            location_id,
            category_customer_id,
            size,
          };
        })
        .groupBy((media) => {
          return _.chain(media).get(["type"]).split("/").first().value();
        })
        .value();
      const updateMediaList = _.mergeWith(
        {},
        mediaList,
        groupUploadSuccessResults,
        (objValue, srcValue) => {
          if (_.isArray(objValue)) {
            return _.concat(objValue, srcValue);
          }
        }
      );
      setMediaList(updateMediaList);
      setIsCloseModal(true);
      setIsModalVisible((prev) => ({ ...prev, isOpen: false }));
    }

    if (!useInManageBlock) {
      Swal.fire({
        position: "center",
        icon: "success",
        title: "Upload Multiple media success",
        showConfirmButton: false,
        timer: 1500,
      }).then(() => {
        setIsCloseModal(true);
        setIsModalVisible((prev) => ({ ...prev, isOpen: false }));
      });
    }

    // console.log("All files uploaded successfully");
  };

  const onHandleClearFileList = () => {
    setFileList([]);
    setProgress({});
    playerRefs.current = [];
  };

  return (
    <Container>
      <Line />
      {_.size(fileList) === 0 && (
        <ContainerDrag>
          <SectionDrag>
            <Dragger
              {...generateDragMultipleUpload({
                customer_id,
                location_id,
                setFileList,
              })}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for multiple files or folder uploads.
              </p>
            </Dragger>
          </SectionDrag>
        </ContainerDrag>
      )}

      {_.size(fileList) > 0 && (
        <ContainerPreview>
          <ContainerAction>
            <Button color={COLOR_RED} onClick={() => onHandleClearFileList()}>
              Clear
            </Button>

            <Button color={COLOR_GREEN} onClick={() => onHandleConfirm()}>
              Confirm Uploads
            </Button>
          </ContainerAction>

          <ContainerMediaPreview>
            {_.map(fileList, (item, index) => {
              const type = _.get(item, ["type"]);
              const file = _.get(item, ["file"]);
              const size = _.get(item, ["size"]);
              const duration = _.get(item, ["duration"]);
              const fileKey = _.get(item, ["fileKey"]);
              const isImage = _.startsWith(type, "image/");

              return (
                <ContainerMediaItem key={index}>
                  <MediaItem>
                    {isImage ? (
                      <img
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                        src={URL.createObjectURL(file)}
                        alt={_.get(file, ["name"], "-")}
                      />
                    ) : (
                      <MemoizedReactPlayer
                        file={file}
                        index={index}
                        playerRefs={playerRefs}
                      />
                    )}
                  </MediaItem>
                  <MediaContent>
                    <div>
                      <Text>Name: {_.get(item, ["display_name"])}</Text>
                      <Text>Size: {formatBytes({ bytes: size })}</Text>
                      <Text>Duration: {duration} Sec.</Text>
                    </div>
                    <div>
                      <Progress
                        style={{ width: "97%" }}
                        percent={_.get(progress, [fileKey])}
                        type="line"
                      />
                    </div>
                  </MediaContent>
                </ContainerMediaItem>
              );
            })}
          </ContainerMediaPreview>
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </ContainerPreview>
      )}
    </Container>
  );
};
